import React, { useState, useEffect } from 'react';
import { Menu, Stethoscope } from 'lucide-react';
import Sidebar from './dashboard/Sidebar';

const PAGE_TITLES: Record<string, string> = {
  // Main pages
  'dashboard': 'Dashboard',
  'visit-planning': 'Visit Planning',
  'doctors': 'Doctors',
  'firms': 'Firms',
  'employees': 'Employees',
  'tour-plans': 'Tour Plans',
  'holidays': 'Holidays',
  'attendance': 'Attendance',
  'expenses': 'Expenses',
  'hr-leave': 'HR & Leave',
  'settings': 'Settings',
  'device-management': 'Device Management',
  'allowance-settings': 'Allowance Settings',
  'doctor-database': 'Doctor Database',
  // Reports
  'reports/visit-tracker': 'Visit Tracker',
  'reports/call-reports': 'Call Reports',
  'reports/missed-calls': 'Missed Calls',
  // Masters
  'master': 'Master',
  'designation': 'Designation',
  'country': 'Country',
  'zone': 'Zone',
  'headquarter': 'Head Quarter',
  'location': 'Location',
  'doctor-master': 'Doctor Master',
  'medicine-master': 'Medicine Master',
  'sfc': 'SFC',
  'route-master': 'Route Master',
  'daily-allowance': 'Daily Allowance',
};

interface PageLayoutProps {
  onLogout?: () => void;
  currentPage?: string;
  onNavigate?: (page: string) => void;
  children: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({ onLogout, currentPage, onNavigate, children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pageTitle = currentPage ? (PAGE_TITLES[currentPage] ?? currentPage) : 'Apex Med';

  useEffect(() => {
    document.title = currentPage ? `${pageTitle} | Apex Med` : 'Apex Med';
  }, [currentPage, pageTitle]);

  const handleNavigate = (page: string) => {
    setSidebarOpen(false);
    onNavigate?.(page);
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={onLogout}
        currentPage={currentPage}
        onNavigate={handleNavigate}
      />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 lg:hidden shrink-0">
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-lg">
              <Stethoscope className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-sm">{pageTitle}</span>
          </div>
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default PageLayout;
